import { canOpen } from "./destinations";
import type { Destination } from "./destinations";
import type { Permission } from "./permissions";

/**
 * The spotlight search's logic, kept out of the dialog so it can be tested
 * without rendering one.
 */

/**
 * Below this many characters the server is not asked for records. One letter
 * matches most of a fleet's ids and every customer with an "a" in their email —
 * three list queries for a result nobody will read.
 */
export const RECORD_MIN_LENGTH = 2;

/** How many of each kind of record the palette shows. */
export const RECORD_LIMIT = 5;

/** The session's permission check, which denies anything not explicitly held. */
export type Can = (permission: Permission) => boolean;

/**
 * Pages the principal may open — the rail's rule exactly, so the palette never
 * offers a page the rail hides.
 */
export function pagesFor(pages: readonly Destination[], can: Can): readonly Destination[] {
  return pages.filter((page) => canOpen(page, can));
}

/** One kind of record the palette searches, and what it takes to search it. */
export interface RecordSource {
  readonly section: string;
  readonly path: string;
  /**
   * Every one must be held: the list's own `.view`, plus the view permission of
   * the page a result opens onto when that is a different page.
   */
  readonly permissions: readonly Permission[];
}

export const RECORD_SOURCES = {
  chargePoints: {
    section: "Charge points",
    path: "/charge-points",
    permissions: ["charge_points.view"],
  },
  // A customer result opens that customer's orders (there is no customer page),
  // so it needs the orders page's permission as well as the directory's.
  customers: {
    section: "Customers",
    path: "/customers",
    permissions: ["customers.view", "public_orders.view"],
  },
  identities: { section: "Identities", path: "/identities", permissions: ["identities.view"] },
} as const satisfies Record<string, RecordSource>;

export function canSearch(source: RecordSource, can: Can): boolean {
  return source.permissions.every((permission) => can(permission));
}

/** The kinds of record the principal may search, in palette order. */
export function recordSourcesFor(can: Can): readonly RecordSource[] {
  return Object.values(RECORD_SOURCES).filter((source) => canSearch(source, can));
}

export function shouldSearchRecords(query: string): boolean {
  return query.trim().length >= RECORD_MIN_LENGTH;
}

/**
 * Pages that match `query`, best first: a label that starts with it, then one
 * with a later word that starts with it, then one that contains it, then one
 * whose group does. Ties keep rail order, which is itself a judgement about
 * what is opened most. An empty query lists every page.
 */
export function rankPages(pages: readonly Destination[], query: string): readonly Destination[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") return pages;

  return pages
    .map((page, index) => ({ page, index, score: scoreOf(page, needle) }))
    .filter(
      (entry): entry is { page: Destination; index: number; score: number } => entry.score !== null,
    )
    .sort((a, b) => a.score - b.score || a.index - b.index)
    .map((entry) => entry.page);
}

function scoreOf(page: Destination, needle: string): number | null {
  const label = page.label.toLowerCase();
  if (label.startsWith(needle)) return 0;
  if (label.split(/\s+/).some((word) => word.startsWith(needle))) return 1;
  if (label.includes(needle)) return 2;
  if (page.group.toLowerCase().includes(needle)) return 3;
  return null;
}

/** The keyboard fields a shortcut check reads; `KeyboardEvent` satisfies it. */
export interface ShortcutKey {
  readonly key: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly altKey: boolean;
  readonly shiftKey: boolean;
  readonly repeat: boolean;
}

/**
 * ⌘K on a Mac, Ctrl+K elsewhere, with nothing else held. Not Ctrl+K on a Mac:
 * there it is "delete to end of line" in every text field, and taking it would
 * break that for anyone who uses it. A held key's auto-repeat is ignored, or
 * holding the chord would flicker the palette open and shut.
 */
export function isSpotlightShortcut(event: ShortcutKey, isMac: boolean): boolean {
  const modifier = isMac ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
  return (
    event.key.toLowerCase() === "k" && modifier && !event.altKey && !event.shiftKey && !event.repeat
  );
}

/** Whether the browser says it is running on Apple hardware. */
export function isMacPlatform(): boolean {
  return typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform);
}

/** The next selected index after an arrow press, wrapping; -1 for an empty list. */
export function stepIndex(current: number, delta: 1 | -1, length: number): number {
  if (length === 0) return -1;
  return (current + delta + length) % length;
}
